/**
 * FAQ Page
 *
 * CMS-managed frequently asked questions with expandable sections.
 * Falls back to hardcoded content when CMS is not configured.
 */

import { Disclosure } from '@headlessui/react';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { Link } from 'react-router';
import { usePageSection } from '@/hooks/usePageContent';
import type { FAQPageContent } from '@/types/pageContent';
import { DEFAULT_FAQ } from '../defaults/faqDefaults';

export const FAQPage = () => {
    const cms = usePageSection<FAQPageContent>('faq', 'content');
    const content = cms?.categories ? cms : DEFAULT_FAQ;
    // FAQPage structured data is emitted from the route shell's `meta` export
    // (`app/routes/$locale.faq.tsx`), using `DEFAULT_FAQ` as the build-time
    // source. The runtime CMS overrides only affect the on-page rendered
    // content, not the JSON-LD payload.

    return (
        <>
            <div className="bg-white py-12 sm:py-16">
                <div className="mx-auto max-w-3xl px-6 lg:px-8">
                    <div className="mb-8">
                        <Link
                            to="/"
                            className="inline-flex items-center text-sm font-medium text-primary hover:text-primary"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Home
                        </Link>
                    </div>

                    <div className="mb-12">
                        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            Frequently Asked Questions
                        </h1>
                        <p className="mt-4 text-muted-foreground">
                            Find answers to common questions about our baby equipment rental service.
                            Can't find what you're looking for?{' '}
                            <Link to="/contact" className="font-medium text-primary hover:text-primary">
                                Contact us
                            </Link>
                        </p>
                    </div>

                    <div className="space-y-6">
                        {content.categories.map((category) => (
                            <div key={category.name} className="rounded-xl bg-muted p-6">
                                <h2 className="mb-4 text-lg font-semibold text-foreground">{category.name}</h2>
                                <div className="space-y-2">
                                    {category.faqs.map((faq, index) => (
                                        <Disclosure key={index} as="div" className="rounded-lg bg-white shadow-sm">
                                            {({ open }) => (
                                                <>
                                                    <dt>
                                                        <Disclosure.Button className="flex w-full items-start justify-between px-5 py-4 text-left">
                                                            <span className="text-base font-medium text-foreground">
                                                                {faq.question}
                                                            </span>
                                                            <span className="ml-6 flex h-7 items-center">
                                                                <ChevronDown
                                                                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                                                                        open ? 'rotate-180' : ''
                                                                    }`}
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                        </Disclosure.Button>
                                                    </dt>
                                                    <Disclosure.Panel as="dd" className="px-5 pb-4">
                                                        <p className="text-sm leading-relaxed text-muted-foreground">
                                                            {faq.answer}
                                                        </p>
                                                    </Disclosure.Panel>
                                                </>
                                            )}
                                        </Disclosure>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 rounded-xl bg-primary p-8 text-center text-white">
                        <h3 className="text-xl font-semibold">Still have questions?</h3>
                        <p className="mt-2 text-primary-foreground/80">
                            Our team is happy to help with any questions you may have.
                        </p>
                        <Link
                            to="/contact"
                            className="mt-4 inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQPage;
